import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileCheck, Clock, CheckCircle, XCircle, Search, Eye,
  Hotel, Utensils, Briefcase, FileText, IndianRupee, Calendar,
  ChevronRight, AlertCircle, BookOpen
} from 'lucide-react';

export default function PendingPostings({ user }) {
  const [postings, setPostings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Dialog states
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState(null);
  
  // Form state
  const [rejectionReason, setRejectionReason] = useState('');
  const [ledgerMappings, setLedgerMappings] = useState({});

  useEffect(() => {
    fetchPostings();
    fetchSummary();
  }, [statusFilter, sourceFilter]);

  const fetchPostings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      
      const response = await api.get(`/postings?${params.toString()}`);
      setPostings(response.data.postings || []);
    } catch (error) {
      toast.error('Failed to fetch postings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/postings/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const viewDetails = async (posting) => {
    try {
      const response = await api.get(`/postings/${posting.id}`);
      setSelectedPosting(response.data);
      setLedgerMappings({});
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to fetch posting details');
    }
  };

  const handleApprove = async () => {
    try {
      const response = await api.post(`/postings/${selectedPosting.id}/approve`, {
        action: 'approve',
        ledger_mappings: Object.keys(ledgerMappings).length > 0 ? ledgerMappings : null
      });
      
      toast.success(response.data.message);
      setShowApproveDialog(false);
      setShowDetailsDialog(false);
      fetchPostings();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve posting'));
    }
  };

  const handleReject = async () => {
    try {
      if (!rejectionReason.trim()) {
        toast.error('Please provide a rejection reason');
        return;
      }
      
      const response = await api.post(`/postings/${selectedPosting.id}/approve`, {
        action: 'reject',
        rejection_reason: rejectionReason
      });
      
      toast.success(response.data.message);
      setShowRejectDialog(false);
      setShowDetailsDialog(false);
      setRejectionReason('');
      fetchPostings();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject posting'));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one posting');
      return;
    }
    
    try {
      const response = await api.post('/postings/bulk-approve', {
        posting_ids: selectedIds,
        action: 'approve'
      });
      
      toast.success(`Approved ${response.data.results.success.length} postings`);
      setSelectedIds([]);
      fetchPostings();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to bulk approve'));
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one posting');
      return;
    }
    
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      const response = await api.post('/postings/bulk-approve', {
        posting_ids: selectedIds,
        action: 'reject',
        rejection_reason: reason
      });
      
      toast.success(`Rejected ${response.data.results.success.length} postings`);
      setSelectedIds([]);
      fetchPostings();
      fetchSummary();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to bulk reject'));
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pendingPostings = postings.filter(p => p.status === 'pending');
    if (selectedIds.length === pendingPostings.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingPostings.map(p => p.id));
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'room_checkout': return <Hotel className="h-4 w-4 text-blue-500" />;
      case 'restaurant_order': return <Utensils className="h-4 w-4 text-orange-500" />;
      case 'service_order': return <Briefcase className="h-4 w-4 text-purple-500" />;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'room_checkout': return 'Room Checkout';
      case 'restaurant_order': return 'Restaurant';
      case 'service_order': return 'Services';
      default: return 'Manual';
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { className: 'bg-yellow-100 text-yellow-700', icon: Clock },
      approved: { className: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      rejected: { className: 'bg-red-100 text-red-700', icon: XCircle },
      posted: { className: 'bg-green-100 text-green-700', icon: FileCheck }
    };
    const config = variants[status] || variants.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredPostings = postings.filter(posting => {
    const searchLower = searchTerm.toLowerCase();
    const sourceNumber = (posting.source_number || posting.source_ref || '').toLowerCase();
    const customerName = (posting.customer_name || '').toLowerCase();
    const description = (posting.description || '').toLowerCase();
    
    return sourceNumber.includes(searchLower) ||
      customerName.includes(searchLower) ||
      description.includes(searchLower);
  });

  return (
    <div className="space-y-6" data-testid="pending-postings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileCheck className="h-8 w-8 text-orange-500" />
            Pending Postings
          </h1>
          <p className="text-gray-600 mt-1">Review and approve revenue transactions</p>
        </div>
        {selectedIds.length > 0 && statusFilter === 'pending' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
            <Button 
              onClick={handleBulkApprove}
              className="bg-green-600 hover:bg-green-700"
              data-testid="bulk-approve-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve All
            </Button>
            <Button 
              variant="destructive"
              onClick={handleBulkReject}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject All
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('pending')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{summary.by_status?.pending || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500 opacity-50" />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {formatCurrency(summary.pending_amount || 0)}
              </p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('approved')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.by_status?.approved || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('posted')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Posted</p>
                  <p className="text-2xl font-bold text-green-600">{summary.by_status?.posted || 0}</p>
                </div>
                <FileCheck className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('rejected')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{summary.by_status?.rejected || 0}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by invoice/order number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-postings"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="room_checkout">Room Checkout</SelectItem>
                <SelectItem value="restaurant_order">Restaurant</SelectItem>
                <SelectItem value="service_order">Services</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Postings Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {statusFilter === 'pending' && (
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectedIds.length === postings.filter(p => p.status === 'pending').length && postings.filter(p => p.status === 'pending').length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                )}
                <TableHead>Source</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={statusFilter === 'pending' ? 8 : 7} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2">Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredPostings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={statusFilter === 'pending' ? 8 : 7} className="text-center py-8 text-gray-500">
                    <FileCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No postings found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPostings.map((posting) => (
                  <TableRow key={posting.id} data-testid={`posting-row-${posting.id}`}>
                    {statusFilter === 'pending' && (
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(posting.id)}
                          onCheckedChange={() => toggleSelection(posting.id)}
                          disabled={posting.status !== 'pending'}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getSourceIcon(posting.source)}
                        <span className="text-sm">{getSourceLabel(posting.source)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm font-medium">{posting.source_number}</span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{posting.customer_name || 'Walk-in'}</p>
                        {posting.room_number && (
                          <p className="text-sm text-gray-500">Room {posting.room_number}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{new Date(posting.source_date).toLocaleDateString()}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium">{formatCurrency(posting.total_amount)}</span>
                    </TableCell>
                    <TableCell>{getStatusBadge(posting.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => viewDetails(posting)}
                        data-testid={`view-posting-${posting.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedPosting && getSourceIcon(selectedPosting.source)}
              Posting Details - {selectedPosting?.source_number}
            </DialogTitle>
            <DialogDescription>
              Review the transaction details before approving
            </DialogDescription>
          </DialogHeader>
          
          {selectedPosting && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-500">Source</Label>
                  <p className="font-medium">{getSourceLabel(selectedPosting.source)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Date</Label>
                  <p className="font-medium">{new Date(selectedPosting.source_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedPosting.status)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Customer</Label>
                  <p className="font-medium">{selectedPosting.customer_name || 'Walk-in'}</p>
                </div>
                {selectedPosting.customer_gstin && (
                  <div>
                    <Label className="text-gray-500">GSTIN</Label>
                    <p className="font-medium font-mono">{selectedPosting.customer_gstin}</p>
                  </div>
                )}
                {selectedPosting.room_number && (
                  <div>
                    <Label className="text-gray-500">Room</Label>
                    <p className="font-medium">{selectedPosting.room_number}</p>
                  </div>
                )}
              </div>
              
              {/* Line Items */}
              <div>
                <Label className="text-gray-500 mb-2 block">Line Items</Label>
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Ledger</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPosting.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <p className="font-medium">{item.description}</p>
                            {item.hsn_sac_code && (
                              <p className="text-xs text-gray-500">HSN: {item.hsn_sac_code}</p>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                          <TableCell>
                            {selectedPosting.status === 'pending' && selectedPosting.available_ledgers?.length > 0 ? (
                              <Select
                                value={ledgerMappings[idx] || item.suggested_ledger_id || ''}
                                onValueChange={(value) => setLedgerMappings({...ledgerMappings, [idx]: value})}
                              >
                                <SelectTrigger className="w-[200px]">
                                  <SelectValue placeholder="Select ledger" />
                                </SelectTrigger>
                                <SelectContent>
                                  {selectedPosting.available_ledgers.map((ledger) => (
                                    <SelectItem key={ledger.id} value={ledger.id}>
                                      {ledger.code} - {ledger.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-sm">
                                {item.final_ledger_name || item.suggested_ledger_name || '-'}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
              
              {/* Totals */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  {/* Tax breakdown */}
                  <Label className="text-gray-500 mb-2 block">Tax Details</Label>
                  <Card>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">CGST</span>
                        <span>{formatCurrency(selectedPosting.cgst_total)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">SGST</span>
                        <span>{formatCurrency(selectedPosting.sgst_total)}</span>
                      </div>
                      {selectedPosting.igst_total > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">IGST</span>
                          <span>{formatCurrency(selectedPosting.igst_total)}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
                <div>
                  {/* Amount breakdown */}
                  <Label className="text-gray-500 mb-2 block">Amount Summary</Label>
                  <Card>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Subtotal</span>
                        <span>{formatCurrency(selectedPosting.subtotal)}</span>
                      </div>
                      {selectedPosting.discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span>- {formatCurrency(selectedPosting.discount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tax</span>
                        <span>{formatCurrency(selectedPosting.cgst_total + selectedPosting.sgst_total + selectedPosting.igst_total)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Total</span>
                        <span>{formatCurrency(selectedPosting.total_amount)}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
              
              {/* Journal Entry Info (if posted) */}
              {selectedPosting.journal_voucher_number && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-700">
                      Posted to Journal: {selectedPosting.journal_voucher_number}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Rejection Reason (if rejected) */}
              {selectedPosting.status === 'rejected' && selectedPosting.rejection_reason && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-red-700">Rejection Reason:</span>
                      <p className="text-red-600 mt-1">{selectedPosting.rejection_reason}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>Close</Button>
            {selectedPosting?.status === 'pending' && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowRejectDialog(true)}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  onClick={() => setShowApproveDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="approve-posting-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve & Post
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this posting? A journal entry will be created and posted to the general ledger.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">{selectedPosting?.source_number}</span>
                <span className="font-bold text-lg">{formatCurrency(selectedPosting?.total_amount || 0)}</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleApprove}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Posting</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="rejection-reason">Rejection Reason *</Label>
            <Textarea
              id="rejection-reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows={3}
              data-testid="rejection-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectionReason(''); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReject}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject Posting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
